import {ErrorNames, productPageFedopsEvent} from '../constants';
import {SPECS} from '../specs';
import {BiButtonActionType} from '@wix/wixstores-client-core/dist/es/src/constants';
import {VolatileCartService} from './VolatileCartService';
import {CheckoutService} from './CheckoutService';
import {
  CheckoutStageWithCartParams,
  ICanCheckoutResponse,
  IProductDTO,
  IPropsInjectedByViewerScript,
  TranslationDictionary,
  UserInput,
} from '../types/app-types';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {MultilingualService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/MultilingualService/MultilingualService';
import {StoreMetaDataService} from './StoreMetaDataService';
import _ from 'lodash';
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor';
import {ReadOnlyExperiments} from '@wix/wix-experiments';
import {ViewerContext} from '@wix/wixstores-client-storefront-common';

export enum CheckoutType {
  BuyNow = 'buyNow',
  Subscribe = 'subscriptionPlans',
  Cashier = 'cashier',
}

export class DirectPurchaseService {
  private readonly checkoutService: CheckoutService;
  private readonly volatileCartService: VolatileCartService;

  constructor(
    private readonly siteStore: SiteStore,
    private readonly viewerContext: ViewerContext,
    private readonly reportError: (e) => any,
    private readonly reportButtonAction: (buttonType: BiButtonActionType) => void,
    private readonly nextProps: (additionalProps: Partial<IPropsInjectedByViewerScript>) => void,
    private readonly fedopsLogger: any,
    private readonly trackEvent: ({checkoutId}: {checkoutId: string}) => void,
    private readonly onSuccess: Function,
    private readonly isEditorX: boolean,
    private readonly multilingualService: MultilingualService,
    private readonly storeMetaDataService: StoreMetaDataService,
    private readonly panoramaClient: ControllerFlowAPI['panoramaClient'],
    private readonly errorHandler: ControllerFlowAPI['errorHandler'],
    private readonly wixCodeApi: ControllerFlowAPI['controllerConfig']['wixCodeApi'],
    private readonly experiments: ReadOnlyExperiments,
    private readonly translations: TranslationDictionary
  ) {
    this.volatileCartService = new VolatileCartService(
      this.siteStore,
      this.storeMetaDataService,
      this.reportError,
      this.errorHandler,
      this.wixCodeApi,
      this.experiments,
      this.nextProps,
      this.translations
    );
    this.checkoutService = new CheckoutService(
      this.siteStore,
      this.viewerContext,
      this.nextProps,
      this.isEditorX,
      this.storeMetaDataService,
      this.experiments
    );
  }

  private readonly getPathFromUrl = (url: string) => {
    return url.split(/[?#]/)[0];
  };

  public handleError = (): void => {
    this.nextProps({errors: {errorName: ErrorNames.StoreInfoError}});
  };

  public checkIsAllowedToCheckoutSync(product: IProductDTO, isSubscribe = false): ICanCheckoutResponse {
    return this.checkoutService.checkIsAllowedToCheckoutSync(product, isSubscribe);
  }

  /* istanbul ignore next: todo: test */
  public handleExpressCashierOnClick = (product: IProductDTO): void => {
    const canCheckoutResponse: ICanCheckoutResponse = this.checkIsAllowedToCheckoutSync(product);
    if (!canCheckoutResponse.canCheckout) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.checkoutService.openModalByType(canCheckoutResponse.modalType);
    }
  };

  public handleBuyNow = async (
    accessibilityEnabled: boolean,
    product: IProductDTO,
    userInputs: UserInput,
    isPreOrderState: boolean
  ): Promise<void> => {
    const isUseCartV2ForDirectPurchase = this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase);
    if (!isUseCartV2ForDirectPurchase) {
      const canCheckoutResponse: ICanCheckoutResponse = await this.checkoutService
        .checkOldIsAllowedToCheckout(product)
        .catch((e) => {
          this.reportError(e);
          return {} as ICanCheckoutResponse;
        });

      if (_.isEmpty(canCheckoutResponse)) {
        this.handleError();
        return;
      }
      /* istanbul ignore next */
      this.reportButtonAction(isPreOrderState ? BiButtonActionType.PreOrderNow : BiButtonActionType.BuyNow);
      if (!canCheckoutResponse.canCheckout) {
        void this.fedopsLogger.interactionEnded(productPageFedopsEvent.BuyNow);
        void this.panoramaClient.transaction(productPageFedopsEvent.BuyNow).finish();
        void this.checkoutService.openModalByType(canCheckoutResponse.modalType);
        return;
      } else {
        return this.handleCheckoutStage(
          accessibilityEnabled,
          product,
          userInputs,
          CheckoutType.BuyNow,
          isPreOrderState
        );
      }
    }

    this.reportButtonAction(isPreOrderState ? BiButtonActionType.PreOrderNow : BiButtonActionType.BuyNow);
    return this.handleCheckoutStage(accessibilityEnabled, product, userInputs, CheckoutType.BuyNow, isPreOrderState);
  };

  public handleSubscribe = async (
    accessibilityEnabled: boolean,
    product: IProductDTO,
    userInputs: UserInput
  ): Promise<void> => {
    const isUseCartV2ForDirectPurchase = this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase);
    if (!isUseCartV2ForDirectPurchase) {
      const canCheckoutResponse: ICanCheckoutResponse = await this.checkoutService
        .checkOldIsAllowedToCheckout(product, true)
        .catch((e) => {
          this.reportError(e);
          return {} as ICanCheckoutResponse;
        });

      if (_.isEmpty(canCheckoutResponse)) {
        this.handleError();
        return;
      }

      this.reportButtonAction(BiButtonActionType.Subscribe);

      if (!canCheckoutResponse.canCheckout) {
        void this.fedopsLogger.interactionEnded(productPageFedopsEvent.Subscribe);
        void this.panoramaClient.transaction(productPageFedopsEvent.Subscribe).finish();
        void this.checkoutService.openModalByType(canCheckoutResponse.modalType);
        return;
      } else {
        return this.handleCheckoutStage(accessibilityEnabled, product, userInputs, CheckoutType.Subscribe, false);
      }
    }

    this.reportButtonAction(BiButtonActionType.Subscribe);
    return this.handleCheckoutStage(accessibilityEnabled, product, userInputs, CheckoutType.Subscribe, false);
  };

  public handleCheckoutStage = async (
    accessibilityEnabled: boolean,
    product: IProductDTO,
    userInputs: UserInput,
    checkoutType: CheckoutType,
    isPreOrderState: boolean
  ) => {
    const {checkoutId} = await this.volatileCartService
      .getStandaloneCheckoutIds(product, userInputs, isPreOrderState)
      .catch(this.reportError);

    /* istanbul ignore next: todo: test */
    if (checkoutId) {
      this.trackEvent({checkoutId});
      await this.handleCheckoutStageWithExistingCart({accessibilityEnabled, checkoutType, checkoutId});
    }
  };

  public handleCheckoutStageWithExistingCart = async ({
    accessibilityEnabled,
    checkoutType,
    cashierPaymentId,
    checkoutId,
  }: CheckoutStageWithCartParams) => {
    const event =
      checkoutType === CheckoutType.Subscribe ? productPageFedopsEvent.Subscribe : productPageFedopsEvent.BuyNow;

    /* istanbul ignore else: todo: test */
    if (checkoutType) {
      this.fedopsLogger.interactionEnded(event);
      this.panoramaClient.transaction(event).finish();
    }

    const [thankYouPageSectionUrl, cartPageSectionUrl, checkoutUrl] = await Promise.all([
      this.siteStore.getSectionUrl(this.siteStore.pageMap.thankyou),
      this.siteStore.getSectionUrl(this.siteStore.pageMap.cart),
      this.siteStore.getSectionUrl(this.siteStore.pageMap.checkout),
    ]).catch(this.reportError);

    const checkoutRelativeUrl = checkoutUrl.relativeUrl;
    const thankYouPageUrl = this.getPathFromUrl(thankYouPageSectionUrl.url);
    const cartPageUrl = this.getPathFromUrl(cartPageSectionUrl.url);
    const deviceType = this.siteStore.isDesktop()
      ? /* istanbul ignore next: todo(ariel): test this */ 'desktop'
      : 'mobile';

    await this.checkoutService.navigateToCheckout({
      checkoutRelativeUrl,
      siteBaseUrl: this.siteStore.location.baseUrl,
      thankYouPageUrl,
      cartUrl: cartPageUrl,
      paymentMethodName: undefined,
      locale: this.viewerContext.locale,
      deviceType,
      a11y: accessibilityEnabled,
      originType: checkoutType,
      cashierPaymentId,
      checkoutId,
    });

    await this.onSuccess();
  };
}
